import Phaser from 'phaser';
import { GAME_WIDTH } from '../config/constants';
import type { ActivePowerUp } from '../systems/PowerUpManager';
import { POWER_UP_CONFIGS } from '../sprites/PowerUp';
import { getCurrentPlayer } from '../config/playerManager';

export class HUD {
  private scene: Phaser.Scene;
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private powerUpContainer!: Phaser.GameObjects.Container;
  private powerUpDisplays: Map<string, { icon: Phaser.GameObjects.Sprite; timer: Phaser.GameObjects.Text; bar: Phaser.GameObjects.Graphics }> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.create();
  }

  private create(): void {
    const player = getCurrentPlayer();

    // Top bar with game title and player name
    this.scene.add.rectangle(GAME_WIDTH / 2, 12, GAME_WIDTH - 60, 20, 0x000000, 0.4);
    
    // Game title (left)
    this.scene.add.text(50, 12, 'ROLLER TOASTER', {
      fontFamily: 'Arial Black',
      fontSize: '12px',
      color: '#ffff00',
    }).setOrigin(0, 0.5);

    // Player name (right)
    this.scene.add.text(GAME_WIDTH - 50, 12, player.name, {
      fontFamily: 'Arial Black',
      fontSize: '12px',
      color: '#00ffff',
    }).setOrigin(1, 0.5);

    // HUD background strip (subtle) - moved down
    const hudBg = this.scene.add.rectangle(GAME_WIDTH / 2, 42, GAME_WIDTH - 60, 40, 0x000000, 0.3);
    hudBg.setStrokeStyle(1, 0x00ffff, 0.3);

    // Score (left side)
    this.scene.add.text(50, 28, 'SCORE', {
      fontFamily: 'Arial',
      fontSize: '10px',
      color: '#00ffff',
    });
    
    this.scoreText = this.scene.add.text(50, 40, '000000', {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffffff',
    });

    // Wave number (center) with glow
    this.scene.add.text(GAME_WIDTH / 2, 36, 'WAVE 1', {
      fontFamily: 'Arial Black',
      fontSize: '20px',
      color: '#ff8800',
    }).setOrigin(0.5, 0).setAlpha(0.4);

    this.waveText = this.scene.add.text(GAME_WIDTH / 2, 36, 'WAVE 1', {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffff00',
    });
    this.waveText.setOrigin(0.5, 0);

    // Lives (right side) with icon
    this.scene.add.text(GAME_WIDTH - 50, 28, 'LIVES', {
      fontFamily: 'Arial',
      fontSize: '10px',
      color: '#00ffff',
    }).setOrigin(1, 0);

    // Lives display with truck icons
    this.livesText = this.scene.add.text(GAME_WIDTH - 50, 40, '10', {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ff6b35',
    }).setOrigin(1, 0);

    // Animated truck icon traversing the HUD row
    const animatedTruck = this.scene.add.sprite(150, 48, 'truck').setScale(0.35);
    animatedTruck.setFlipX(true); // Start facing right
    
    let movingRight = true;
    
    // Create exhaust puff effect
    const createExhaustPuff = () => {
      if (!animatedTruck.active) return;
      
      // Position exhaust at back of truck (depends on direction)
      const exhaustX = movingRight ? animatedTruck.x - 15 : animatedTruck.x + 15;
      const exhaustY = animatedTruck.y + 5;
      
      const puff = this.scene.add.circle(exhaustX, exhaustY, 3, 0x888888, 0.6);
      
      this.scene.tweens.add({
        targets: puff,
        x: movingRight ? exhaustX - 20 : exhaustX + 20,
        y: exhaustY - 10,
        alpha: 0,
        scale: 2,
        duration: 600,
        ease: 'Power2',
        onComplete: () => puff.destroy()
      });
    };
    
    // Exhaust timer
    this.scene.time.addEvent({
      delay: 300,
      callback: createExhaustPuff,
      loop: true
    });
    
    // Animate truck back and forth across the HUD (slower)
    this.scene.tweens.add({
      targets: animatedTruck,
      x: GAME_WIDTH - 150,
      duration: 8000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onYoyo: () => {
        animatedTruck.setFlipX(false); // Face left when going back
        movingRight = false;
      },
      onRepeat: () => {
        animatedTruck.setFlipX(true); // Face right when going forward
        movingRight = true;
      }
    });

    // Power-up display area (below HUD)
    this.powerUpContainer = this.scene.add.container(50, 80);

    // Power-ups label
    this.scene.add.text(50, 68, 'POWER-UPS', {
      fontFamily: 'Arial',
      fontSize: '9px',
      color: '#666666',
    });
  }

  updateScore(score: number): void {
    this.scoreText.setText(score.toString().padStart(6, '0'));
  }

  updateLives(lives: number): void {
    this.livesText.setText(lives.toString());
    
    // Flash red when lives decrease
    this.livesText.setTint(0xff0000);
    this.scene.time.delayedCall(150, () => {
      this.livesText.clearTint();
    });
  }

  updateWave(wave: number): void {
    this.waveText.setText(`WAVE ${wave}`);
    
    // Flash effect for new wave
    this.scene.tweens.add({
      targets: this.waveText,
      scale: 1.3,
      duration: 200,
      yoyo: true,
      ease: 'Power2',
    });
  }

  showMessage(message: string, duration: number = 2000): void {
    const text = this.scene.add.text(GAME_WIDTH / 2, 200, message, {
      fontFamily: 'Arial Black',
      fontSize: '32px',
      color: '#00ffff',
      stroke: '#003333',
      strokeThickness: 4,
    });
    text.setOrigin(0.5);

    this.scene.tweens.add({
      targets: text,
      alpha: 0,
      y: text.y - 50,
      duration: duration,
      ease: 'Power2',
      onComplete: () => {
        text.destroy();
      }
    });
  }

  updatePowerUps(activePowerUps: ActivePowerUp[]): void {
    const currentTime = this.scene.time.now;
    
    // Clear old displays
    this.powerUpDisplays.forEach((display, type) => {
      const isStillActive = activePowerUps.find(p => p.type === type);
      if (!isStillActive) {
        display.icon.destroy();
        display.timer.destroy();
        display.bar.destroy();
        this.powerUpDisplays.delete(type);
      }
    });

    // Update or create displays
    activePowerUps.forEach((powerUp, index) => {
      const config = POWER_UP_CONFIGS[powerUp.type];
      const yOffset = index * 35;
      
      let display = this.powerUpDisplays.get(powerUp.type);
      
      if (!display) {
        // Create new display
        const icon = this.scene.add.sprite(0, yOffset, config.texture);
        icon.setScale(0.8);
        
        const timer = this.scene.add.text(25, yOffset - 5, '', {
          fontFamily: 'Arial Black',
          fontSize: '14px',
          color: '#ffffff',
        });
        
        const bar = this.scene.add.graphics();
        
        this.powerUpContainer.add([icon, timer, bar]);
        
        display = { icon, timer, bar };
        this.powerUpDisplays.set(powerUp.type, display);
      }
      
      // Update position in case order changed
      display.icon.setY(yOffset);
      display.timer.setY(yOffset - 5);
      
      // Update timer
      const timeLeft = Math.max(0, (powerUp.endTime - currentTime) / 1000);
      
      if (powerUp.type === 'butterShield' && powerUp.shieldHits !== undefined) {
        display.timer.setText(`${powerUp.shieldHits} hits`);
      } else {
        display.timer.setText(`${timeLeft.toFixed(1)}s`);
      }
      
      // Update timer bar
      display.bar.clear();
      const barWidth = 60;
      const barHeight = 4;
      const progress = timeLeft / (config.duration / 1000);
      
      display.bar.fillStyle(0x333333, 1);
      display.bar.fillRect(25, yOffset + 10, barWidth, barHeight);
      display.bar.fillStyle(config.color, 1);
      display.bar.fillRect(25, yOffset + 10, barWidth * progress, barHeight);
    });
  }
}

